import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CommentViewModel, CommentFormData, CommentResolutionStatus, CommentAuthorRole } from '../../../shared/models/comment.model';
import { CommentsService } from '../../../shared/services/comments.service';
import { UserService } from '../../../core/services/user.service';
import { Observable, Subscription } from 'rxjs';

@Component({
  selector: 'app-comments-section',
  templateUrl: './comments-section.component.html',
  styleUrls: ['./comments-section.component.scss'],
  standalone: false
})
export class CommentsSectionComponent implements OnInit, OnChanges, OnDestroy {
  @Input() pipeClassId: string;
  @Input() revisionId: string;
  @Input() contextType: 'summary' | 'components' | 'valves' | 'branchtables' = 'summary'; // Updated context types
  @Input() isEditMode: boolean = false;
  @Input() canEdit: boolean = false; // Controls add/edit/delete comment actions
  /** When true the section is visible in read-only mode even when canEdit is false.
   *  Use for users in the revision workflow (reviewers) who should see comments but not edit fields. */
  @Input() canView: boolean = false;
  @Input() comments: CommentViewModel[] = []; // Comments from parent component
  /** True while the parent is still fetching comments. Shows a skeleton instead of a
   *  misleading "Comments (0)" count that briefly flashes before the real data arrives. */
  @Input() commentsLoading$: Observable<boolean>;
  // Both authors and reviewers can change resolution status on any root-level comment
  @Input() canChangeResolutionStatus: boolean = false;

  @Output() commentsChanged = new EventEmitter<CommentViewModel[]>();
  @Output() saveCommentsRequested = new EventEmitter<void>(); // New event for auto-save

  // Local working copy of comments
  workingComments: CommentViewModel[] = [];
  newCommentForm: FormGroup;
  replyForms: { [commentId: string]: FormGroup } = {};
  editForms: { [commentId: string]: FormGroup } = {};

  // UI state management
  showNewCommentForm = false;
  activeReplyId: string | null = null;
  activeEditId: string | null = null;
  expandedThreads: Set<string> = new Set();

  // Current user - obtained from auth service
  currentUser: { id: string; name: string } | null = null;
  private userSubscription: Subscription = new Subscription();

  // Expose enum to template
  readonly CommentResolutionStatus: typeof CommentResolutionStatus = CommentResolutionStatus;
  readonly CommentAuthorRole: typeof CommentAuthorRole = CommentAuthorRole;

  // Tracks which comment has an in-flight resolution-status update to prevent double-clicks
  updatingResolutionStatusIds: Set<string> = new Set();

  constructor(
    private formBuilder: FormBuilder,
    private cdr: ChangeDetectorRef,
    private commentsService: CommentsService,
    private userService: UserService
  ) {
    // Initialize main comment form
    this.newCommentForm = this.formBuilder.group({
      content: ['', [Validators.required, Validators.minLength(3)]]
    });
  }

  ngOnInit(): void {
    this.initializeCurrentUser();
    this.initializeWorkingComments();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['comments'] && changes['comments'].currentValue) {
      this.initializeWorkingComments();
    }
  }

  ngOnDestroy(): void {
    this.userSubscription.unsubscribe();
  }

  /**
   * Initialize current user from auth service
   */
  private initializeCurrentUser(): void {
    // Get current user immediately if available
    const account = this.userService.getCurrentUser();
    if (account) {
      this.setCurrentUserFromAccount(account);
    }

    // Subscribe to user profile changes
    this.userSubscription.add(
      this.userService.currentUserProfile$.subscribe(userAccount => {
        if (userAccount) {
          this.setCurrentUserFromAccount(userAccount);
        }
      })
    );
  }

  /**
   * Set current user from Azure AD account info
   */
  private setCurrentUserFromAccount(account: any): void {
    this.currentUser = {
      id:  account.localAccountId,
      // account.name is the display name from the ID token (e.g. "Ratti, Pablo Ezequiel").
      // account.username is the UPN/email — not suitable for authorName.
      name: account.name || account.username
    };

    this.cdr.detectChanges();
  }

  /**
   * Initialize working comments from input
   */
  private initializeWorkingComments(): void {
    this.workingComments = [...this.comments];
    this.cdr.detectChanges();
  }

  /**
   * Shows the new comment form
   */
  startNewComment(): void {
    // Security validation: Check permissions before showing form
    if (!this.canCreateComment()) {
      console.warn('User is not authorized to create comments');
      return;
    }

    this.showNewCommentForm = true;
    this.cancelActiveActions(); // Cancel any active replies or edits
  }

  /**
   * Submits a new root comment via the dedicated POST /api/Comments endpoint.
   * Using the single-create endpoint (instead of save-bulk) ensures the BE returns the
   * new comment with authorId populated from the JWT, so author-specific controls
   * (delete/edit buttons) appear immediately without needing a full refetch.
   */
  submitNewComment(): void {
    // Security validation: Check permissions before allowing new comment
    if (!this.canCreateComment()) {
      console.warn('User is not authorized to create comments');
      return;
    }

    if (this.newCommentForm.valid && this.currentUser) {
      const formData = this.newCommentForm.value as CommentFormData;
      this.newCommentForm.reset();
      this.showNewCommentForm = false;

      this.commentsService.createComment({
        pipeClassId: this.pipeClassId,
        revisionId: this.revisionId,
        contextType: this.contextType,
        content: formData.content,
        authorName: this.currentUser.name
      }).subscribe({
        next: (savedComment) => {
          // BE returns the comment with the real GUID and authorId from JWT.
          savedComment.level = 0;
          this.workingComments.unshift(savedComment);
          this.emitCommentsChanged();
          this.cdr.detectChanges();
        },
        error: () => {
          // Silently swallow — the global HTTP error interceptor already shows a snack-bar.
          this.cdr.detectChanges();
        }
      });
    }
  }

  /**
   * Cancels new comment creation
   */
  cancelNewComment(): void {
    this.showNewCommentForm = false;
    this.newCommentForm.reset();
  }

  /**
   * Starts replying to a specific comment
   */
  startReply(commentId: string): void {
    const parentComment = this.findComment(commentId);

    // Security validation: Check permissions before allowing reply
    if (!this.canReplyToComment(parentComment)) {
      console.warn('User is not authorized to reply to comments');
      return;
    }

    this.cancelActiveActions();
    this.activeReplyId = commentId;

    // Create reply form if it doesn't exist
    if (!this.replyForms[commentId]) {
      this.replyForms[commentId] = this.formBuilder.group({
        content: ['', [Validators.required, Validators.minLength(3)]]
      });
    } else {
      this.replyForms[commentId].reset();
    }

    // Expand the thread to show the reply form
    this.expandedThreads.add(commentId);
  }

  /**
   * Submits a reply to a comment
   */
  submitReply(parentCommentId: string): void {
    const replyForm = this.replyForms[parentCommentId];
    const parentComment = this.findComment(parentCommentId);

    // Security validation: Check permissions before allowing reply
    if (!this.canReplyToComment(parentComment)) {
      console.warn('User is not authorized to reply to this comment');
      return;
    }

    if (replyForm && replyForm.valid && this.currentUser) {
      const formData = replyForm.value as CommentFormData;

      if (parentComment) {
        replyForm.reset();
        this.activeReplyId = null;

        // Call the dedicated reply endpoint — authorId comes from the JWT on the BE side.
        this.commentsService.addReply(parentCommentId, this.currentUser.name, formData.content)
          .subscribe({
            next: (savedReply) => {
              // Level is one deeper than parent
              savedReply.level = parentComment.level + 1;
              parentComment.addReply(savedReply);
              this.emitCommentsChanged();
              this.cdr.detectChanges();
            },
            error: () => {
              // Silently swallow — the snack-bar interceptor already shows an error
              this.cdr.detectChanges();
            }
          });
      }
    }
  }

  /**
   * Cancels replying to a comment
   */
  cancelReply(commentId: string): void {
    if (this.replyForms[commentId]) {
      this.replyForms[commentId].reset();
    }
    this.activeReplyId = null;
  }

  /**
   * Starts editing a comment
   */
  startEdit(commentId: string): void {
    const comment = this.findComment(commentId);

    // Security validation: Only the author can edit their comment
    if (!comment || !this.canEditComment(comment)) {
      console.warn('User is not authorized to edit this comment');
      return;
    }

    if (comment) {
      this.cancelActiveActions();
      this.activeEditId = commentId;

      // Create edit form if it doesn't exist
      if (!this.editForms[commentId]) {
        this.editForms[commentId] = this.formBuilder.group({
          content: [comment.content, [Validators.required, Validators.minLength(3)]]
        });
      } else {
        this.editForms[commentId].patchValue({ content: comment.content });
      }
    }
  }

  /**
   * Submits comment edit via the dedicated PUT /api/Comments/{id} endpoint.
   * Only the comment author can call this (enforced by BE).
   * Using the single-edit endpoint (instead of save-bulk) ensures the response
   * contains the updated comment with all BE-resolved fields, and avoids
   * accidentally replacing other comments in the tree.
   */
  submitEdit(commentId: string): void {
    const editForm = this.editForms[commentId];
    const comment = this.findComment(commentId);

    // Security validation: Only the author can edit their comment
    if (!comment || !this.canEditComment(comment)) {
      console.warn('User is not authorized to edit this comment');
      return;
    }

    if (editForm && editForm.valid) {
      const formData = editForm.value as CommentFormData;
      this.activeEditId = null;

      this.commentsService.updateComment({
        id: commentId,
        content: formData.content,
        resolutionStatus: comment.resolutionStatus
      }).subscribe({
        next: (updatedComment) => {
          // Patch only the edited fields on the existing comment object so the
          // surrounding tree (replies, level, etc.) is not disturbed.
          comment.content = updatedComment.content;
          comment.updatedDate = updatedComment.updatedDate;
          comment.isEdited = true;
          this.emitCommentsChanged();
          this.cdr.detectChanges();
        },
        error: () => {
          // Silently swallow — the global HTTP error interceptor already shows a snack-bar.
          this.cdr.detectChanges();
        }
      });
    }
  }

  /**
   * Cancels comment editing
   */
  cancelEdit(commentId: string): void {
    if (this.editForms[commentId]) {
      this.editForms[commentId].reset();
    }
    this.activeEditId = null;
  }

  /**
   * Toggles thread expansion
   */
  toggleThread(commentId: string): void {
    if (this.expandedThreads.has(commentId)) {
      this.expandedThreads.delete(commentId);
    } else {
      this.expandedThreads.add(commentId);
    }
  }

  /**
   * Checks if a thread is expanded
   */
  isThreadExpanded(commentId: string): boolean {
    return this.expandedThreads.has(commentId);
  }

  /**
   * Checks if current user can edit a comment (only the author can edit their own comment).
   * Both field-editors (canEdit) and reviewers (canView) may edit their own comments.
   */
  canEditComment(comment: CommentViewModel): boolean {
    return this.currentUser &&
           (this.canEdit || this.canView) &&
           comment.authorId === this.currentUser.id;
  }

  /**
   * Checks if current user can reply to a comment.
   * The BE only supports one level of nesting — replying to a reply returns 400.
   * Both field-editors (canEdit) and reviewers (canView) may post replies.
   */
  canReplyToComment(comment?: CommentViewModel): boolean {
    return this.currentUser && (this.canEdit || this.canView) && (comment?.level ?? 0) === 0;
  }

  /**
   * Checks if current user can create new comments.
   * Both authors (canEdit=true) and reviewers (canView=true) are allowed to post
   * comments — commenting is a review action, not a field-editing action.
   */
  canCreateComment(): boolean {
    return this.currentUser && (this.canEdit || this.canView);
  }

  /**
   * Checks if current user is the author of a comment
   */
  isCommentAuthor(comment: CommentViewModel): boolean {
    return this.currentUser && comment.authorId === this.currentUser.id;
  }

  /**
   * Gets formatted relative time for display
   */
  getRelativeTime(date: Date): string {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString();
  }

  /**
   * Gets all comments in a flat structure for iteration
   */
  getAllCommentsFlat(): CommentViewModel[] {
    const allComments: CommentViewModel[] = [];

    for (const comment of this.workingComments) {
      allComments.push(comment);
      allComments.push(...comment.getAllRepliesFlat());
    }

    return allComments;
  }

  /**
   * Gets total comment count including replies
   */
  getTotalCommentCount(): number {
    let count = this.workingComments.length;
    for (const comment of this.workingComments) {
      count += comment.getTotalReplyCount();
    }
    return count;
  }

  // Private helper methods

  /**
   * Recursively removes a comment by ID from the tree (mutates the array in place).
   */
  private removeCommentFromTree(comments: CommentViewModel[], commentId: string): boolean {
    const idx = comments.findIndex(c => c.id === commentId);
    if (idx !== -1) {
      comments.splice(idx, 1);
      return true;
    }
    for (const comment of comments) {
      if (this.removeCommentFromTree(comment.replies, commentId)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Finds a comment by ID in the entire comment tree
   */
  private findComment(commentId: string): CommentViewModel | null {
    const allComments = this.getAllCommentsFlat();
    return allComments.find(c => c.id === commentId) || null;
  }

  /**
   * Generates a unique comment ID
   */
  private generateCommentId(): string {
    return `comment-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Cancels all active actions (reply, edit)
   */
  private cancelActiveActions(): void {
    this.activeReplyId = null;
    this.activeEditId = null;
  }

  /**
   * Emits the comments changed event
   */
  private emitCommentsChanged(): void {
    this.commentsChanged.emit(this.workingComments);
  }

  /**
   * Requests the parent component to save comments
   */
  private requestSaveComments(): void {
    this.saveCommentsRequested.emit();
  }

  /**
   * Checks if current user can delete a comment.
   * Only the author can delete, and only when the comment has no replies
   * (the backend returns 500 if it has replies).
   * Both field-editors (canEdit) and reviewers (canView) may delete their own comments.
   */
  canDeleteComment(comment: CommentViewModel): boolean {
    return this.currentUser &&
           (this.canEdit || this.canView) &&
           comment.authorId === this.currentUser.id &&
           comment.replies.length === 0;
  }

  /**
   * Deletes a comment via the dedicated DELETE endpoint and removes it from the
   * local working-copy tree on success. The BE returns 202 with the deleted
   * CommentViewModel, confirming which comment was removed without a refetch.
   * Guards against deleting comments with replies at the UI level to avoid a BE 500.
   */
  deleteComment(commentId: string): void {
    const comment = this.findComment(commentId);
    if (!comment || !this.canDeleteComment(comment)) {
      return;
    }

    this.commentsService.deleteComment({ id: commentId }).subscribe({
      next: () => {
        // Use the returned deleted comment to confirm removal from local tree.
        this.removeCommentFromTree(this.workingComments, commentId);
        this.emitCommentsChanged();
        this.cdr.detectChanges();
      },
      error: () => {
        // Silently swallow — the global HTTP error interceptor already shows a snack-bar.
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Returns the display label for the author's role in the revision.
   * null means the comment predates role-tracking and shows a generic fallback.
   */
  getAuthorRoleLabel(role: CommentAuthorRole | null): string {
    switch (role) {
      case CommentAuthorRole.Author:           return 'Revision Author';
      case CommentAuthorRole.Approver:         return 'Approver';
      case CommentAuthorRole.InternalReviewer: return 'Internal Reviewer';
      case CommentAuthorRole.ExternalReviewer: return 'External Reviewer';
      default:                                 return 'Internal Reviewer'; // null = legacy
    }
  }

  /**
   * Returns the human-readable label for a resolution status value.
   */
  getResolutionStatusLabel(status: CommentResolutionStatus): string {
    switch (status) {
      case CommentResolutionStatus.Adopted: return 'Adopted';
      case CommentResolutionStatus.Closed:   return 'Closed';
      default:                               return 'Open';
    }
  }

  /**
   * Sends a PATCH request to update the resolution status of a root-level comment.
   * Updates the local working copy optimistically so the UI reflects the change immediately.
   */
  updateResolutionStatus(comment: CommentViewModel, newStatus: CommentResolutionStatus): void {
    if (!this.currentUser || this.updatingResolutionStatusIds.has(comment.id)) {
      return;
    }

    // Optimistic update so the UI responds instantly
    const previousStatus: CommentResolutionStatus = comment.resolutionStatus;
    comment.resolutionStatus = newStatus;
    this.updatingResolutionStatusIds.add(comment.id);
    this.emitCommentsChanged();
    this.cdr.detectChanges();

    this.commentsService.updateCommentResolutionStatus(comment.id, newStatus)
      .subscribe({
        next: (updatedComment) => {
          comment.resolutionStatus = updatedComment.resolutionStatus;
          this.updatingResolutionStatusIds.delete(comment.id);
          this.emitCommentsChanged();
          this.cdr.detectChanges();
        },
        error: () => {
          // Roll back optimistic update if the API call fails
          comment.resolutionStatus = previousStatus;
          this.updatingResolutionStatusIds.delete(comment.id);
          this.emitCommentsChanged();
          this.cdr.detectChanges();
        }
      });
  }

  /**
   * TrackBy function for comment list performance
   */
  trackByCommentId(index: number, comment: CommentViewModel): string {
    return comment.id;
  }
}
